/**
 * In-portal SharePoint folder browser (Graph-backed).
 * Browse / preview / download here; edit opens SharePoint in a new tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall, getAuthToken } from '../../utils/apiClient';
import { toSharePointHref } from '../../utils/sharepointUrl';
import { bookmarkUrlKey } from '../../utils/bookmarkUrl';

const PREVIEWABLE = /\.(pdf|png|jpe?g|gif|webp|txt|csv)$/i;
const IMAGE = /\.(png|jpe?g|gif|webp)$/i;

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function scopeQuery({ projectId, estimateId, rootUrl, itemId, driveId }) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', String(projectId));
  if (estimateId) params.set('estimateId', String(estimateId));
  if (rootUrl) params.set('url', rootUrl);
  if (itemId) params.set('itemId', itemId);
  if (driveId) params.set('driveId', driveId);
  return params.toString();
}

/**
 * @param {object} props
 * @param {number|null} props.projectId
 * @param {number|null} props.estimateId
 * @param {string|null} props.rootUrl — crawler root or bookmark override
 * @param {string} [props.rootLabel]
 * @param {Set<string>} [props.bookmarkedKeys]
 * @param {(item: object) => void} [props.onQuickBookmark]
 * @param {boolean} [props.bookmarking]
 */
const SharePointFolderBrowser = ({
  projectId = null,
  estimateId = null,
  rootUrl = null,
  rootLabel = 'SharePoint',
  bookmarkedKeys = null,
  onQuickBookmark = null,
  bookmarking = false,
}) => {
  const { instance, accounts } = useMsal();
  const [crumbs, setCrumbs] = useState([]); // [{ id, name, driveId }]
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [driveId, setDriveId] = useState(null);
  const [resolvedRootWebUrl, setResolvedRootWebUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { item, downloadUrl }

  const load = useCallback(async (folderItemId = null, folderDriveId = null, nextCrumbs = []) => {
    if (!rootUrl || (!projectId && !estimateId) || !accounts[0]) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const qs = scopeQuery({
        projectId,
        estimateId,
        rootUrl,
        itemId: folderItemId,
        driveId: folderDriveId,
      });
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/sharepoint/browse?${qs}`);
      const data = res.data?.data;
      if (!data) throw new Error('Empty browse response');
      setItems(data.items || []);
      setCurrent(data.current || null);
      setDriveId(data.current?.driveId || data.root?.driveId || folderDriveId || null);
      setResolvedRootWebUrl(data.root?.webUrl || data.rootUrl || rootUrl);
      setCrumbs(nextCrumbs);
    } catch (err) {
      console.error('SharePoint browse failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to browse SharePoint');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, estimateId, rootUrl]);

  useEffect(() => {
    setCrumbs([]);
    load(null, null, []);
  }, [load]);

  const openFolder = (item) => {
    if (!item?.isFolder) return;
    const nextDriveId = item.driveId || driveId || null;
    load(item.id, nextDriveId, [
      ...crumbs,
      { id: item.id, name: item.name, driveId: nextDriveId },
    ]);
  };

  const goCrumb = (index) => {
    if (index < 0) {
      load(null, null, []);
      return;
    }
    const target = crumbs[index];
    load(target.id, target.driveId || null, crumbs.slice(0, index + 1));
  };

  const openExternal = (webUrl) => {
    const href = toSharePointHref(webUrl);
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const loadPreview = async (item) => {
    const activeDriveId = item?.driveId || driveId;
    if (!activeDriveId || !item?.id) return;
    if (!PREVIEWABLE.test(item.name || '')) {
      openExternal(item.webUrl);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const qs = scopeQuery({ projectId, estimateId, rootUrl });
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/sharepoint/item?${qs}&driveId=${encodeURIComponent(activeDriveId)}&itemId=${encodeURIComponent(item.id)}`
      );
      const meta = res.data?.item;
      if (!meta?.downloadUrl) {
        openExternal(meta?.webUrl || item.webUrl);
        return;
      }
      setPreview({ item: meta, downloadUrl: meta.downloadUrl });
    } catch (err) {
      console.error('SharePoint preview failed:', err);
      setError(err.response?.data?.message || 'Failed to preview file');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (item) => {
    const activeDriveId = item?.driveId || driveId;
    if (!activeDriveId || !item?.id || !accounts[0]) return;
    try {
      const token = await getAuthToken(instance, accounts);
      const qs = scopeQuery({ projectId, estimateId, rootUrl });
      const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/?$/, '');
      const apiBase = base.endsWith('/api') ? base : `${base}/api`;
      const url = `${apiBase}/sharepoint/download?${qs}&driveId=${encodeURIComponent(activeDriveId)}&itemId=${encodeURIComponent(item.id)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = item.name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('SharePoint download failed:', err);
      setError(err.message || 'Failed to download file');
    }
  };

  if (!rootUrl) {
    return (
      <div style={styles.empty}>
        No root SharePoint link yet — filled overnight by the crawler. You can still add personal bookmarks below.
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <div style={styles.crumbs}>
          <button type="button" onClick={() => goCrumb(-1)} style={styles.crumbBtn}>
            {rootLabel || 'Root'}
          </button>
          {crumbs.map((c, i) => (
            <span key={`${c.id}-${i}`}>
              <span style={styles.sep}>/</span>
              <button type="button" onClick={() => goCrumb(i)} style={styles.crumbBtn}>
                {c.name}
              </button>
            </span>
          ))}
        </div>
        <div style={styles.toolbarActions}>
          {current?.webUrl || resolvedRootWebUrl ? (
            <button
              type="button"
              onClick={() => openExternal(current?.webUrl || resolvedRootWebUrl)}
              style={styles.externalBtn}
            >
              Open in SharePoint ↗
            </button>
          ) : null}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.listPane}>
          {loading && !items.length ? (
            <div style={styles.mutedPad}>Loading…</div>
          ) : null}
          {!loading && !error && items.length === 0 ? (
            <div style={styles.mutedPad}>This folder is empty.</div>
          ) : null}
          <ul style={styles.list}>
            {items.map((it) => (
              <li key={`${it.driveId || 'd'}-${it.id}`} style={styles.row}>
                <button
                  type="button"
                  onClick={() => (it.isFolder ? openFolder(it) : loadPreview(it))}
                  style={styles.rowMain}
                  title={it.name}
                >
                  <span style={styles.icon}>{it.isFolder ? '📁' : '📄'}</span>
                  <span style={styles.name}>{it.name}</span>
                  <span style={styles.meta}>
                    {it.isFolder ? '' : formatSize(it.size)}
                  </span>
                </button>
                <div style={styles.rowActions}>
                  {!it.isFolder && PREVIEWABLE.test(it.name || '') && (
                    <button type="button" style={styles.smallBtn} onClick={() => loadPreview(it)}>
                      Preview
                    </button>
                  )}
                  {!it.isFolder && (
                    <button type="button" style={styles.smallBtn} onClick={() => downloadFile(it)}>
                      Download
                    </button>
                  )}
                  {it.webUrl && onQuickBookmark && (
                    <button
                      type="button"
                      style={{
                        ...styles.starBtn,
                        color: bookmarkedKeys && bookmarkedKeys.has(bookmarkUrlKey(it.webUrl)) ? '#d97706' : '#94a3b8',
                      }}
                      title={
                        bookmarkedKeys && bookmarkedKeys.has(bookmarkUrlKey(it.webUrl))
                          ? 'Remove bookmark'
                          : `Bookmark this ${it.isFolder ? 'folder' : 'file'}`
                      }
                      disabled={bookmarking}
                      onClick={() => onQuickBookmark(it)}
                    >
                      {bookmarkedKeys && bookmarkedKeys.has(bookmarkUrlKey(it.webUrl)) ? '★' : '☆'}
                    </button>
                  )}
                  {it.webUrl && (
                    <button type="button" style={styles.smallBtn} onClick={() => openExternal(it.webUrl)}>
                      Open ↗
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {preview && (
          <div style={styles.previewPane}>
            <div style={styles.previewHeader}>
              <strong style={{ fontSize: 13 }}>{preview.item.name}</strong>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" style={styles.smallBtn} onClick={() => downloadFile(preview.item)}>
                  Download
                </button>
                {preview.item.webUrl && (
                  <button type="button" style={styles.smallBtn} onClick={() => openExternal(preview.item.webUrl)}>
                    Open in SharePoint ↗
                  </button>
                )}
                <button type="button" style={styles.smallBtn} onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>
            <div style={styles.previewBody}>
              {IMAGE.test(preview.item.name || '') ? (
                <img src={preview.downloadUrl} alt={preview.item.name} style={styles.previewImg} />
              ) : /\.pdf$/i.test(preview.item.name || '') ? (
                <iframe title={preview.item.name} src={preview.downloadUrl} style={styles.previewFrame} />
              ) : (
                <iframe title={preview.item.name} src={preview.downloadUrl} style={styles.previewFrame} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrap: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    overflow: 'hidden',
    marginBottom: '1rem',
  },
  empty: {
    padding: '1rem',
    color: '#64748b',
    fontSize: '0.9rem',
    fontStyle: 'italic',
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    marginBottom: '1rem',
    background: '#f8fafc',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    flexWrap: 'wrap',
  },
  crumbs: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 2,
    fontSize: 13,
    minWidth: 0,
  },
  crumbBtn: {
    background: 'none',
    border: 'none',
    color: '#0f766e',
    cursor: 'pointer',
    fontWeight: 600,
    padding: '2px 4px',
    fontSize: 13,
  },
  sep: { color: '#94a3b8', margin: '0 2px' },
  toolbarActions: { display: 'flex', gap: 8 },
  externalBtn: {
    padding: '6px 10px',
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  error: {
    margin: '10px 14px 0',
    padding: '8px 10px',
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 6,
    fontSize: 13,
  },
  body: {
    display: 'flex',
    minHeight: 320,
    maxHeight: '58vh',
  },
  listPane: {
    flex: 1,
    overflow: 'auto',
    minWidth: 0,
  },
  mutedPad: {
    padding: 24,
    textAlign: 'center',
    color: '#64748b',
    fontSize: 13,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: '6px 0',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '2px 10px',
    borderBottom: '1px solid #f1f5f9',
  },
  rowMain: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '8px 4px',
    minWidth: 0,
    fontSize: 13,
    color: '#0f172a',
  },
  icon: { width: 22, flexShrink: 0 },
  name: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    color: '#94a3b8',
    fontSize: 11,
    flexShrink: 0,
    minWidth: 48,
    textAlign: 'right',
  },
  rowActions: {
    display: 'flex',
    gap: 4,
    flexShrink: 0,
    alignItems: 'center',
  },
  starBtn: {
    padding: '2px 6px',
    fontSize: 16,
    lineHeight: 1,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  smallBtn: {
    padding: '4px 8px',
    fontSize: 11,
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    cursor: 'pointer',
    color: '#334155',
    whiteSpace: 'nowrap',
  },
  previewPane: {
    width: '46%',
    borderLeft: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 280,
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  previewBody: {
    flex: 1,
    overflow: 'auto',
    background: '#0f172a0a',
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
    margin: '0 auto',
    padding: 8,
  },
  previewFrame: {
    width: '100%',
    height: '100%',
    minHeight: 280,
    border: 'none',
  },
};

export default SharePointFolderBrowser;
